import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToasterService } from '../../../core/services/toaster.service';
import { IconComponent } from '../../icons/icon.component';
import {
  ToasterConfig,
  ToasterType,
} from '../loader/loader.component';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="fixed z-[10001] pointer-events-none" [ngClass]="getPositionClasses()">
      @for (toaster of toasters; track toaster.id) {
        <div
          @slideIn
          class="pointer-events-auto mb-3 min-w-[320px] max-w-md rounded-lg shadow-lg border backdrop-blur-sm relative"
          [ngClass]="getToasterClasses(toaster.type)"
        >
          <div class="flex items-start gap-3 p-4">
            <!-- Icon -->
            <div class="flex-shrink-0 mt-0.5">
              @if (toaster.type === 'success') {
                <app-icon name="check" [size]="20" [ngClass]="getIconClasses(toaster.type)" />
              } @else if (toaster.type === 'error') {
                <app-icon name="x" [size]="20" [ngClass]="getIconClasses(toaster.type)" />
              } @else if (toaster.type === 'warning') {
                <app-icon name="triangleAlert" [size]="20" [ngClass]="getIconClasses(toaster.type)" />
              } @else {
                <app-icon name="info" [size]="20" [ngClass]="getIconClasses(toaster.type)" />
              }
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              @if (toaster.title) {
                <h4 class="text-sm font-semibold mb-1" [ngClass]="getTitleClasses(toaster.type)">
                  {{ toaster.title }}
                </h4>
              }
              <p class="text-sm" [ngClass]="getMessageClasses(toaster.type)">
                {{ toaster.message }}
              </p>
              
              @if (toaster.action) {
                <button
                  (click)="handleAction(toaster)"
                  class="mt-2 text-xs font-medium hover:underline"
                  [ngClass]="getActionClasses(toaster.type)"
                >
                  {{ toaster.action.label }}
                </button>
              }
            </div>

            <!-- Close Button -->
            @if (toaster.closable) {
              <button
                (click)="close(toaster.id)"
                class="flex-shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                [ngClass]="getCloseButtonClasses(toaster.type)"
              >
                <app-icon name="x" [size]="16" />
              </button>
            }
          </div>

          <!-- Progress Bar -->
          @if (toaster.duration && toaster.duration > 0) {
            <div class="absolute bottom-0 left-0 right-0 h-1 bg-black/5 dark:bg-white/5 rounded-b-lg overflow-hidden">
              <div
                class="h-full toaster-progress-bar"
                [ngClass]="getProgressClasses(toaster.type)"
                [style.animation-duration.ms]="toaster.duration"
              ></div>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .toaster-progress-bar {
      animation: progress linear forwards;
    }
    
    @keyframes progress {
      from { width: 100%; }
      to { width: 0%; }
    }

    p {
      white-space: pre-wrap;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class ToasterComponent implements OnInit {
  private toasterService = inject(ToasterService);
  toasters: ToasterConfig[] = [];
  private timeouts = new Map<string, any>();

  ngOnInit() {
    this.toasterService.toasters$.subscribe((toasters) => {
      const previousToasterIds = new Set(this.toasters.map(t => t.id));
      const currentToasterIds = new Set(toasters.map(t => t.id));

      // Clear timeouts for removed toasters
      this.timeouts.forEach((timeout, id) => {
        if (!currentToasterIds.has(id)) {
          clearTimeout(timeout);
          this.timeouts.delete(id);
        }
      });

      // Set timeouts only for new toasters
      toasters.forEach((toaster) => {
        if (!previousToasterIds.has(toaster.id) && toaster.duration && toaster.duration > 0) {
          const timeout = setTimeout(() => {
            this.close(toaster.id);
          }, toaster.duration);
          this.timeouts.set(toaster.id, timeout);
        }
      });

      this.toasters = toasters;
    });
  }

  close(id: string) {
    const timeout = this.timeouts.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(id);
    }
    this.toasterService.remove(id);
  }

  handleAction(toaster: ToasterConfig) {
    if (toaster.action?.onClick) {
      toaster.action.onClick();
    }
    // Always close after action
    this.close(toaster.id);
  }

  getPositionClasses(): string {
    // Default to bottom-right
    return 'bottom-4 right-4';
  }

  getIcon(type: ToasterType): string {
    const icons: Record<ToasterType, string> = {
      success: 'check',
      error: 'x',
      warning: 'triangleAlert',
      info: 'info',
    };
    return icons[type];
  }

  getToasterClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'bg-green-50/95 dark:bg-green-950/90 border-green-200 dark:border-green-800',
      error: 'bg-red-50/95 dark:bg-red-950/90 border-red-200 dark:border-red-800',
      warning: 'bg-yellow-50/95 dark:bg-yellow-950/90 border-yellow-200 dark:border-yellow-800',
      info: 'bg-blue-50/95 dark:bg-blue-950/90 border-blue-200 dark:border-blue-800',
    };
    return classes[type];
  }

  getIconClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'text-green-600 dark:text-green-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-blue-600 dark:text-blue-400',
    };
    return classes[type];
  }

  getTitleClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'text-green-900 dark:text-green-100',
      error: 'text-red-900 dark:text-red-100',
      warning: 'text-yellow-900 dark:text-yellow-100',
      info: 'text-blue-900 dark:text-blue-100',
    };
    return classes[type];
  }

  getMessageClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'text-green-700 dark:text-green-300',
      error: 'text-red-700 dark:text-red-300',
      warning: 'text-yellow-700 dark:text-yellow-300',
      info: 'text-blue-700 dark:text-blue-300',
    };
    return classes[type];
  }

  getActionClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'text-green-700 dark:text-green-400',
      error: 'text-red-700 dark:text-red-400',
      warning: 'text-yellow-700 dark:text-yellow-400',
      info: 'text-blue-700 dark:text-blue-400',
    };
    return classes[type];
  }

  getCloseButtonClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'text-green-600 dark:text-green-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-blue-600 dark:text-blue-400',
    };
    return classes[type];
  }

  getProgressClasses(type: ToasterType): string {
    const classes: Record<ToasterType, string> = {
      success: 'bg-green-500 dark:bg-green-400',
      error: 'bg-red-500 dark:bg-red-400',
      warning: 'bg-yellow-500 dark:bg-yellow-400',
      info: 'bg-blue-500 dark:bg-blue-400',
    };
    return classes[type];
  }
}
